// frontend/src/features/bapb/ImportWizard.jsx
import React, { useMemo, useState } from "react";
import api from "../../apiClient";

const FIELD_DEFS = [
  {
    key: "account_number",
    label: "Account / Parcel #",
    required: true,
    synonyms: [
      "account", "account #", "acct", "acct #", "parcel", "parcel #",
      "parcel acct", "parcel acct num", "parcel_acct_num", "account_number"
    ],
  },
  { key: "site_name", label: "Site / Asset Name", synonyms: ["site", "site name", "asset", "asset name", "property", "property name", "title"] },
  { key: "address", label: "Address", synonyms: ["address", "street", "street address"] },
  { key: "city", label: "City", synonyms: ["city", "town"] },
  { key: "state", label: "State", synonyms: ["state", "st"] },
  { key: "zip", label: "ZIP", synonyms: ["zip", "zipcode", "postal", "postal code"] },
  { key: "client_name", label: "Client", synonyms: ["client", "client name", "company", "company name"] },
  { key: "owner_name", label: "Owner", synonyms: ["owner", "owner name", "parcel assessee", "assessee"] },
  { key: "assessor", label: "Assessor", synonyms: ["assessor", "cad", "county", "jurisdiction"] },
  { key: "property_type", label: "Property Type", synonyms: ["property type", "type", "prop type"] },
  { key: "year_built", label: "Year Built", synonyms: ["year built", "built", "yr built"] },
  { key: "unit_type", label: "Unit Type", synonyms: ["unit type", "units type", "sf", "s.f.", "sqft units"] },
  { key: "units", label: "Units", synonyms: ["units", "sqft", "sf", "square feet", "net rentable", "nra"] },
  { key: "client_site_code", label: "Client Site Code", synonyms: ["client site code", "site code"] },
  { key: "cad_location_code", label: "CAD Location Code", synonyms: ["cad location code", "cad code", "location code"] },

  { key: "previous_year_value", label: "Previous Year Value", synonyms: ["previous year value", "prior value", "prior year value", "py value"] },
  { key: "current_year_value", label: "Current Year Value", synonyms: ["current year value", "value", "fmv", "market value", "cy value"] },

  { key: "opinion_of_value", label: "Opinion of Value", synonyms: ["opinion of value", "opinion", "opv"] },
  { key: "opinion_source", label: "Opinion Source", synonyms: ["opinion source", "source"] },

  { key: "consultant_name", label: "Consultant Name", synonyms: ["consultant", "consultant name"] },
  { key: "consultant_phone", label: "Consultant Phone", synonyms: ["consultant phone", "phone"] },
  { key: "consultant_email", label: "Consultant Email", synonyms: ["consultant email", "email"] },

  { key: "notes", label: "Notes", synonyms: ["notes", "internal notes", "memo"] },
];

function normHeader(s) {
  return (s || "").toString().trim().toLowerCase().replace(/\s+/g, " ");
}

function toNumberish(v) {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  // strip $ and commas
  const cleaned = s.replace(/[$,]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : s;
}

function autoMap(headers) {
  const hNorm = headers.map((h) => ({ raw: h, norm: normHeader(h) }));
  const mapping = {};
  for (const f of FIELD_DEFS) {
    let found = null;

    // exact synonym match
    for (const syn of f.synonyms || []) {
      const synN = normHeader(syn);
      const hit = hNorm.find((h) => h.norm === synN);
      if (hit) { found = hit.raw; break; }
    }

    // contains match fallback
    if (!found) {
      for (const syn of f.synonyms || []) {
        const synN = normHeader(syn);
        const hit = hNorm.find((h) => h.norm.includes(synN) || synN.includes(h.norm));
        if (hit) { found = hit.raw; break; }
      }
    }

    if (found) mapping[f.key] = found;
  }
  return mapping;
}

function parseXlsxFile(file) {
  return new Promise((resolve, reject) => {
    if (!window.XLSX) {
      reject(new Error("XLSX library not loaded (window.XLSX missing)."));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = window.XLSX.read(data, { type: "array" });
        const sheetName = wb.SheetNames?.[0];
        if (!sheetName) throw new Error("No sheets found in workbook.");
        const ws = wb.Sheets[sheetName];
        const json = window.XLSX.utils.sheet_to_json(ws, { defval: "" }); // array of objects keyed by headers
        resolve(json);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsArrayBuffer(file);
  });
}

export default function ImportWizard({ onDone, onCancel }) {
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [stage, setStage] = useState("upload"); // upload | map | review | done
  const [rawRows, setRawRows] = useState([]); // from XLSX (array of objects)
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [selected, setSelected] = useState({}); // row_idx -> bool
  const [preview, setPreview] = useState(null); // server preview response
  const [commitResult, setCommitResult] = useState(null);

  const drafts = useMemo(() => {
    // convert XLSX rows -> {row_idx, account_number, title, property_info}
    const out = [];
    for (let i = 0; i < rawRows.length; i++) {
      const r = rawRows[i] || {};
      const get = (k) => {
        const col = mapping?.[k];
        if (!col) return "";
        return r?.[col] ?? "";
      };

      const acct = String(get("account_number") || "").trim();
      const pi = {};

      // map all non-empty mapped fields into property_info
      for (const f of FIELD_DEFS) {
        if (f.key === "account_number") continue;
        const v = get(f.key);
        if (v === "" || v == null) continue;

        // numeric-like for common numeric fields
        if (["units", "year_built", "previous_year_value", "current_year_value", "opinion_of_value"].includes(f.key)) {
          pi[f.key] = toNumberish(v);
        } else {
          pi[f.key] = String(v).trim();
        }
      }

      if (acct) pi.parcel = acct;
      if (!pi.unit_type && (pi.units || "").toString().match(/[0-9]/)) {
        // default unit type if units is present but unit_type missing
        pi.unit_type = "units";
      }

      const title = (String(get("site_name") || "").trim() || acct);

      out.push({
        row_idx: i + 1, // 1-based for user friendliness
        account_number: acct,
        title,
        property_info: pi,
      });
    }
    return out;
  }, [rawRows, mapping]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    setBusy(true);
    try {
      const rows = await parseXlsxFile(file);
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("No data rows found in the first sheet.");
      }
      const hs = Object.keys(rows[0] || {});
      setRawRows(rows);
      setHeaders(hs);
      const m = autoMap(hs);
      setMapping(m);
      // default select all
      const sel = {};
      for (let i = 0; i < rows.length; i++) sel[i + 1] = true;
      setSelected(sel);
      setStage("map");
    } catch (ex) {
      setErr(ex?.message || String(ex));
    } finally {
      setBusy(false);
    }
  }

  function setMapField(key, headerName) {
    setMapping((prev) => ({ ...(prev || {}), [key]: headerName || "" }));
  }

  async function runPreview() {
    setErr("");
    setBusy(true);
    try {
      const payloadRows = drafts.map((d) => ({
        row_idx: d.row_idx,
        account_number: d.account_number,
        title: d.title,
        property_info: d.property_info,
      }));
      const res = await api.post("/bapb/import/bulk/preview", {
        tax_year: Number(taxYear),
        rows: payloadRows,
      });
      setPreview(res?.data || null);
      setStage("review");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || String(ex));
    } finally {
      setBusy(false);
    }
  }

  async function runCommit() {
    setErr("");
    setBusy(true);
    try {
      const selectedRows = drafts.filter((d) => selected?.[d.row_idx]);
      const res = await api.post("/bapb/import/bulk/commit", {
        tax_year: Number(taxYear),
        skip_existing: true,
        rows: selectedRows.map((d) => ({
          row_idx: d.row_idx,
          account_number: d.account_number,
          title: d.title,
          property_info: d.property_info,
        })),
      });
      setCommitResult(res?.data || null);
      setStage("done");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || String(ex));
    } finally {
      setBusy(false);
    }
  }

  const previewByRowIdx = useMemo(() => {
    const map = {};
    const rows = preview?.rows || [];
    for (const r of rows) map[r.row_idx] = r;
    return map;
  }, [preview]);

  const counts = useMemo(() => {
    const created = commitResult?.created?.length || 0;
    const skippedExisting = commitResult?.skipped_existing?.length || 0;
    const skippedInvalid = commitResult?.skipped_invalid?.length || 0;
    const failed = commitResult?.failed?.length || 0;
    return { created, skippedExisting, skippedInvalid, failed };
  }, [commitResult]);

  return (
    <div>
      <h3 className="text-base font-semibold mb-3">Bulk Import (Excel)</h3>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tax Year</label>
          <input
            type="number"
            value={taxYear}
            onChange={(e) => setTaxYear(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Upload Excel (.xlsx)</label>
          <input type="file" accept=".xlsx" onChange={handleUpload} disabled={busy} />
        </div>
      </div>

      {err && <div className="text-sm text-red-600 mb-3">{err}</div>}

      {stage === "upload" && (
        <div className="text-sm text-gray-600">
          Upload an Excel file where <b>each row becomes a packet</b>. You’ll map columns, then review for duplicates before importing.
        </div>
      )}

      {stage === "map" && (
        <div className="space-y-4">
          <div className="text-sm text-gray-700">
            <b>{rawRows.length.toLocaleString()}</b> rows loaded. Map your Excel columns to packet cover fields.
          </div>

          <div className="rounded-xl border bg-white p-3">
            <div className="text-sm font-semibold mb-2">Column Mapping</div>

            <div className="grid md:grid-cols-2 gap-3">
              {FIELD_DEFS.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs text-gray-500 mb-1">
                    {f.label} {f.required ? <span className="text-red-600">*</span> : null}
                  </label>
                  <select
                    value={mapping?.[f.key] || ""}
                    onChange={(e) => setMapField(f.key, e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                  >
                    <option value="">— Not mapped —</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="mt-3 flex gap-2">
              <button
                disabled={busy}
                onClick={runPreview}
                className="rounded-lg px-3 py-2 border border-green-200 text-green-700 bg-white hover:border-green-300"
              >
                {busy ? "Working…" : "Next: Review"}
              </button>
              <button
                onClick={onCancel}
                className="rounded-lg px-3 py-2 border border-gray-200 bg-white hover:border-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {stage === "review" && (
        <div className="space-y-3">
          <div className="text-sm text-gray-700">
            Review rows before import. Rows flagged as <b>duplicate in DB</b> will be skipped (default).
          </div>

          <div className="rounded-xl border bg-white overflow-auto">
            <table className="min-w-[1000px] w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="py-2 px-3">Import</th>
                  <th className="py-2 px-3">Row</th>
                  <th className="py-2 px-3">Account</th>
                  <th className="py-2 px-3">Site</th>
                  <th className="py-2 px-3">City</th>
                  <th className="py-2 px-3">Assessor</th>
                  <th className="py-2 px-3">Flags</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => {
                  const p = previewByRowIdx[d.row_idx] || {};
                  const flags = [];
                  if (p.errors?.length) flags.push("INVALID");
                  if (p.duplicate_in_file) flags.push("DUP IN FILE");
                  if (p.duplicate_in_db) flags.push("EXISTS IN DB");
                  const flagText = flags.length ? flags.join(", ") : "—";

                  const disabled = Boolean(p.duplicate_in_db || (p.errors && p.errors.length));
                  const checked = Boolean(selected?.[d.row_idx]);

                  return (
                    <tr key={d.row_idx} className="border-t">
                      <td className="py-2 px-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={(e) =>
                            setSelected((prev) => ({ ...(prev || {}), [d.row_idx]: e.target.checked }))
                          }
                        />
                      </td>
                      <td className="py-2 px-3">{d.row_idx}</td>
                      <td className="py-2 px-3 font-mono">{d.account_number || "—"}</td>
                      <td className="py-2 px-3">{d.property_info?.site_name || d.title || "—"}</td>
                      <td className="py-2 px-3">{d.property_info?.city || "—"}</td>
                      <td className="py-2 px-3">{d.property_info?.assessor || "—"}</td>
                      <td className="py-2 px-3">
                        <span className={flags.length ? "text-amber-700" : "text-gray-500"}>
                          {flagText}
                        </span>
                        {p.existing_packet_id ? (
                          <span className="text-gray-500"> (id {p.existing_packet_id})</span>
                        ) : null}
                        {p.errors?.length ? (
                          <div className="text-xs text-red-600 mt-1">{p.errors.join("; ")}</div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2">
            <button
              disabled={busy}
              onClick={runCommit}
              className="rounded-lg px-3 py-2 border border-green-200 text-green-700 bg-white hover:border-green-300"
            >
              {busy ? "Importing…" : "Import Selected"}
            </button>
            <button
              onClick={() => setStage("map")}
              className="rounded-lg px-3 py-2 border border-gray-200 bg-white hover:border-gray-300"
            >
              Back to Mapping
            </button>
            <button
              onClick={onCancel}
              className="rounded-lg px-3 py-2 border border-gray-200 bg-white hover:border-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {stage === "done" && (
        <div className="space-y-3">
          <div className="rounded-xl border bg-white p-4">
            <div className="text-sm font-semibold mb-2">Import Complete</div>
            <div className="text-sm text-gray-700 space-y-1">
              <div>Created: <b>{counts.created}</b></div>
              <div>Skipped (already existed): <b>{counts.skippedExisting}</b></div>
              <div>Skipped (invalid): <b>{counts.skippedInvalid}</b></div>
              <div>Failed: <b>{counts.failed}</b></div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onDone?.({ summary: counts })}
              className="rounded-lg px-3 py-2 border border-sky-200 text-sky-700 bg-white hover:border-sky-300"
            >
              Back to Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
